import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';
import { useReseller } from '../../contexts/ResellerContext';
import { useAuth, userFromDecodedJwt } from '../../contexts/AuthContext';
import domainCorrections from '../../assets/domain_corrections.json';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// Map plan values to readable names
const planNames = {
  'B': 'Free Trial',
  'S': 'Starter',
  'P': 'Pro',
  'U': 'Ultimate'
};

/** Default legal page when reseller has no termsUrl from the API. */
const DEFAULT_TERMS_URL =
  'https://chattitude.ai/index.php?option=com_sppagebuilder&view=page&id=45';

export default function SignupForm({ setLoggedIn }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { reseller } = useReseller();
  const { setUser } = useAuth();
  const productName = reseller?.productName || 'Chattitude';
  const termsAndPrivacyLabel = `${productName} Terms of Service and Privacy Policy`;
  const termsHref =
    (reseller?.termsUrl && String(reseller.termsUrl).trim()) || DEFAULT_TERMS_URL;
  /** Stripe checkout only on main product or when reseller explicitly enables it. */
  const signupUsesStripe =
    reseller == null || reseller.useStripeForBilling === true;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Email verification states
  const [verificationStep, setVerificationStep] = useState('email'); // 'email', 'code', 'form'
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  // Adding six digit functionality 
  const [verificationDigits, setVerificationDigits] = useState(['', '', '', '', '', '']);
  const digitRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  // Get the plan from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const planId = queryParams.get('plan');
  const planName = planNames[planId] || 'Selected';
  // Get billing cycle from URL query parameters (monthly|annual). Default to 'monthly'
  const billingCycleParam = queryParams.get('billing_cycle') || 'monthly';

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    tenantName: '',
    agreeTerms: false
  });

  // Check if company exists
  const checkCompanyExists = async (companyName) => {
    if (!companyName.trim()) {
      setCompanyError('');
      return;
    }

    try {
      const response = await axios.get('/account/tenantExists', {
        params: { tenantName: companyName }
      });

      if (response.data.exists) {
        setCompanyError('Company name already exists');
      } else {
        setCompanyError('');
      }
    } catch (error) {
      console.error('Error checking company name:', error);
    }
  };

  // Check if email exists
  const checkEmailExists = async (email) => {
    if (!email.trim()) {
      setEmailError('');
      return;
    }

    try {
      const response = await axios.get('/users/emailAddrExists', {
        params: { emailAddr: email }
      });

      if (response.data.exists) {
        setEmailError('Email address already exists');
      } else {
        setEmailError('');
      }
    } catch (error) {
      console.error('Error checking email address:', error);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'agreeTerms' ? checked : value
    });

    // Clear errors when user starts typing
    if (name === 'tenantName') {
      setCompanyError('');
    }
    if (name === 'email') {
      setEmailError('');
    }
  };

  // Add this with your other handler functions
  const handleCancel = () => {
    navigate('/signup');
  };

  // Password visibility handlers
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Add these handler functions
  const handleVerificationDigitChange = (index, value) => {
    // Only allow a single digit
    if (value.length > 1) {
      value = value.charAt(value.length - 1);
    }

    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    // Update the digit at the specified index
    const newDigits = [...verificationDigits];
    newDigits[index] = value;
    setVerificationDigits(newDigits);
    setVerificationError('');

    // If a digit was entered and there's a next input, focus it
    if (value && index < 5) {
      digitRefs[index + 1].current.focus();
    }

    // Update the full verification code
    setVerificationCode(newDigits.join(''));
  };

  const handleKeyDown = (index, e) => {
    // If backspace is pressed and the current field is empty, focus the previous field
    if (e.key === 'Backspace' && !verificationDigits[index] && index > 0) {
      digitRefs[index - 1].current.focus();
    }
  };

  const handleVerificationCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    // Check if pasted content is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newDigits = pastedData.split('');
      setVerificationDigits(newDigits);
      setVerificationCode(pastedData);

      // Focus the last input
      digitRefs[5].current.focus();
    }
  };


  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Phone number formatting and validation
  const handlePhoneChange = (event) => {
    const { value } = event.target;

    // If user is trying to delete the extension format, handle it specially
    if (formData.phone.includes(' ext.') && value.length < formData.phone.length) {
      // If deleting from the extension part, remove the whole extension
      if (value.endsWith(' ext.')) {
        setFormData({
          ...formData,
          phone: value.substring(0, value.length - 6)
        });
        return;
      }
    }

    // Remove all non-numeric characters
    let phoneNumber = value.replace(/\D/g, '');

    // Format the phone number as (XXX) XXX-XXXX ext. XXXX
    let formattedNumber = '';

    if (phoneNumber.length > 0) {
      // Format area code
      formattedNumber = '(' + phoneNumber.substring(0, Math.min(3, phoneNumber.length));

      if (phoneNumber.length > 3) {
        // Format first part of number
        formattedNumber += ') ' + phoneNumber.substring(3, Math.min(6, phoneNumber.length));

        if (phoneNumber.length > 6) {
          // Format second part of number
          formattedNumber += '-' + phoneNumber.substring(6, Math.min(10, phoneNumber.length));

          if (phoneNumber.length > 10) {
            // Format extension if present
            formattedNumber += ' ext. ' + phoneNumber.substring(10);
          }
        }
      }
    }

    // Update form data with formatted number
    setFormData({
      ...formData,
      phone: formattedNumber
    });
  };

  // Function to validate phone number
  const isValidPhoneNumber = (phone) => {
    // Extract just the digits
    const digits = phone.replace(/\D/g, '');

    // Must have at least 10 digits (area code + 7-digit number)
    return digits.length >= 10;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email ||
      // !formData.username ||
      !formData.password || !formData.tenantName) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if there's a company error
    if (companyError) {
      setError('Please fix the company name error');
      return;
    }

    // Check if there's an email error
    if (emailError) {
      setError('Please fix the email address error');
      return;
    }

    // Email format validation
    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Business email validation (disabled)
    // if (!isBusinessEmail(formData.email)) {
    //     setError('Please use a business email address');
    //     return;
    // }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.agreeTerms) {
      setError(`You must agree to the ${termsAndPrivacyLabel}`);
      return;
    }

    // Validate phone number
    if (!isValidPhoneNumber(formData.phone)) {
      setError('Please enter a valid phone number with area code');
      return;
    }

    // Domain correction suggestion
    const correction = getDomainCorrection(formData.email);
    if (correction) {
      setError(`It looks like you may have mistyped the domain, did you mean ${correction}?`);
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      // Add the selected plan to the form data
      const dataToSubmit = {
        ...formData,
        plan: planId
        ,
        // include billing cycle so backend can persist billing_cycle on tenant
        billingCycle: billingCycleParam,
        // base_url of the client for reseller lookup and tenant.resellerId
        baseUrl: window.location.hostname
      };

      console.log(dataToSubmit);

      const response = await axios.post(`${API_URL}/account/createAccount`, dataToSubmit);
      console.log('Account created successfully:', response.data);

      // Auto-login: set JWT cookies so the user is authenticated
      const jwtToken = response.data.jwtToken;
      if (jwtToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
        Cookies.setJwtToken(jwtToken);
        try {
          const jsonToken = JSON.parse(atob(jwtToken.split('.')[1]));
          Cookies.setTenantId(jsonToken.tenantId);
          Cookies.setCookies('fname', jsonToken.firstName);
          Cookies.setCookies('lname', jsonToken.lastName);
          Cookies.setCookies('email', jsonToken.email);
          Cookies.setCookies('hasAgent', jsonToken.hasAgent);
          setUser(userFromDecodedJwt(jsonToken));
          setLoggedIn(true);
        } catch (e) {
          console.error('Failed to decode JWT', e);
        }
      }

      const intendedPlan = response.data.intendedPlan;
      if (intendedPlan && ['S', 'P', 'U'].includes(intendedPlan)) {
        setSuccess(true);
        setError('');
        if (signupUsesStripe) {
          try {
            const checkoutRes = await axios.post(`${API_URL}/payment/create-checkout-session`, {
              plan: intendedPlan,
              billing_cycle: response.data.billingCycle || billingCycleParam
            });
            if (checkoutRes.data.success && checkoutRes.data.url) {
              window.location.href = checkoutRes.data.url;
              return;
            }
          } catch (stripeErr) {
            console.error('Stripe checkout session failed:', stripeErr);
          }
        }
        // No Stripe (reseller billing) or checkout failed — go to Dashboard; user can pay from /complete-payment if applicable
        window.location.href = '/#/Dashboard';
        return;
      }

      // Beta / free plan: go straight to dashboard
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/#/Dashboard';
      }, 2000);

    } catch (err) {
      if (err.response && err.response.status === 409 && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred during registration. Please try again.');
      }
      console.error('Registration error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email validation
  const isValidEmail = (email) => {
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Business email validation (disabled)
  // const isBusinessEmail = (email) => {
  //     // List of common personal email domains to reject
  //     const personalDomains = [
  //         'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  //         'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
  //         'zoho.com', 'yandex.com', 'gmx.com', 'live.com',
  //         'msn.com', 'me.com', 'inbox.com', 'comcast.net'
  //     ];
  //
  //     // Extract domain from email
  //     const domain = email.split('@')[1]?.toLowerCase();
  //
  //     // Check if domain is in the personal domains list
  //     return domain && !personalDomains.includes(domain);
  // };

  // Email changes with validation
  const handleEmailChange = (event) => {
    const { value } = event.target;

    // Update the form data
    setFormData({
      ...formData,
      email: value
    });
  };

  // Handle verification email change
  const handleVerificationEmailChange = (e) => {
    setVerificationEmail(e.target.value);
    setVerificationError('');
  };

  // Handle verification code change
  const handleVerificationCodeChange = (e) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setVerificationCode(value);
    setVerificationError('');
  };

  // Send verification code
  const handleSendCode = async () => {
    // Validate email
    if (!isValidEmail(verificationEmail)) {
      setVerificationError('Please enter a valid email address');
      return;
    }

    // Business email validation (disabled)
    // if (!isBusinessEmail(verificationEmail)) {
    //     setVerificationError('Please use a business email address');
    //     return;
    // }

    setSendingCode(true);
    setVerificationError('');

    try {
      // Call backend to send verification code (ensure no /api prefix)
      const response = await axios.post(`${API_URL}/account/sendVerificationCode`, {
        email: verificationEmail,
        baseUrl: window.location.hostname
      });
      if (response.data && response.data.success) {
        setVerificationStep('code');
      } else {
        setVerificationError(response.data?.message || 'Failed to send verification code.');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setVerificationError(err.response.data.message);
      } else {
        setVerificationError('Failed to send verification code. Please try again.');
      }
      console.error('Verification code error:', err);
    } finally {
      setSendingCode(false);
    }
  };

  // Verify code
  const handleVerifyCode = async () => {
    // Validate code length
    if (verificationCode.length !== 6 || verificationDigits.some(digit => digit === '')) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    setVerifyingCode(true);
    setVerificationError('');

    try {
      // Call backend to verify the code (ensure no /api prefix)
      const response = await axios.post(`${API_URL}/account/verifyCode`, {
        email: verificationEmail,
        code: verificationCode
      });
      if (response.data && response.data.success) {
        // Move to form step and pre-populate email
        setFormData({
          ...formData,
          email: verificationEmail
        });
        setVerificationStep('form');
      } else {
        setVerificationError(response.data?.message || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setVerificationError(err.response.data.message);
      } else {
        setVerificationError('Invalid verification code. Please try again.');
      }
      console.error('Code verification error:', err);
    } finally {
      setVerifyingCode(false);
    }
  };

  // Redirect if no plan was selected
  useEffect(() => {
    if (!planId) {
      navigate('/signup');
    }
  }, [planId, navigate]);

  if (!planId) {
    return null; // Don't render anything while redirecting
  }

  // Render email verification dialog
  const renderEmailVerification = () => (
    <Dialog open={verificationStep === 'email'} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h5" align="center">
          Verify Your Email
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            To continue with your {planName} ({billingCycleParam === 'annual' ? 'Annual' : 'Monthly'}) Plan registration, please enter your email address.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            variant="outlined"
            margin="normal"
            value={verificationEmail}
            onChange={handleVerificationEmailChange}
            error={!!verificationError || !!getDomainCorrection(verificationEmail)}
            helperText={
              verificationError
                ? verificationError
                : getDomainCorrection(verificationEmail)
                  ? `It looks like you may have mistyped the domain, did you mean ${getDomainCorrection(verificationEmail)}?`
                  : ''
            }
            disabled={sendingCode}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={handleCancel}
          color="secondary"
          disabled={sendingCode}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendCode}
          disabled={!verificationEmail || sendingCode || !!getDomainCorrection(verificationEmail)}
          startIcon={sendingCode && <CircularProgress size={20} color="inherit" />}
        >
          {sendingCode ? 'Sending...' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render code verification dialog
  const renderCodeVerification = () => (
    <Dialog open={verificationStep === 'code'} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h5" align="center">
          Enter Verification Code
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            We've sent a 6-digit verification code to <strong>{verificationEmail}</strong>.
            Please enter the code below to continue.
          </Typography>

          {/* 6-digit verification code input */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              my: 3
            }}
            onPaste={handleVerificationCodePaste}
          >
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <TextField
                key={index}
                inputRef={digitRefs[index]}
                value={verificationDigits[index]}
                onChange={(e) => handleVerificationDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={verifyingCode}
                variant="outlined"
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    padding: '10px',
                    width: '20px'
                  }
                }}
                sx={{ width: '50px' }}
              />
            ))}
          </Box>

          {verificationError && (
            <Typography color="error" align="center" variant="body2">
              {verificationError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={() => setVerificationStep('email')}
          color="secondary"
          disabled={verifyingCode}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleVerifyCode}
          disabled={verificationCode.length !== 6 || verifyingCode}
          startIcon={verifyingCode && <CircularProgress size={20} color="inherit" />}
        >
          {verifyingCode ? 'Verifying...' : 'Verify Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Domain correction function
  const getDomainCorrection = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return null;
    return domainCorrections[domain] || null;
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Email verification dialog */}
      {renderEmailVerification()}

      {/* Code verification dialog */}
      {renderCodeVerification()}

      {/* Main form with backdrop when in verification process */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={verificationStep !== 'form'}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper elevation={3} sx={{ opacity: verificationStep !== 'form' ? 0.3 : 1 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Sign Up for {planName} Plan
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {['S', 'P', 'U'].includes(planId)
                ? 'Account created! Redirecting to payment...'
                : 'Registration successful! Redirecting...'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Show domain correction error at the top if present */}
            {getDomainCorrection(formData.email) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                It looks like you may have mistyped the domain, did you mean <strong>{getDomainCorrection(formData.email)}</strong>? Please correct your email before submitting.
              </Alert>
            )}
            {/* First Name and Last Name in one row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={isSubmitting || verificationStep !== 'form'}
                sx={{ flex: 1 }}
              />
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={isSubmitting || verificationStep !== 'form'}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Email - full width */}
            <Box sx={{ mb: 2 }}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                onBlur={(e) => checkEmailExists(e.target.value)}
                disabled={isSubmitting || verificationStep !== 'form'}
                error={!!emailError}
                helperText={emailError}
              />
            </Box>

            {/* Phone - full width */}
            <Box sx={{ mb: 2 }}>
              <TextField
                required
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                disabled={isSubmitting || verificationStep !== 'form'}
                helperText="Format: (123) 456-7890 ext. 1234"
                inputProps={{
                  maxLength: 25, // Allow enough characters for the full format with extension
                }}
              />
            </Box>

            { }
            <Box sx={{ mb: 2 }}>
              <TextField
                required
                fullWidth
                label="Company"
                name="tenantName"
                value={formData.tenantName}
                onChange={handleChange}
                onBlur={(e) => checkCompanyExists(e.target.value)}
                disabled={isSubmitting || verificationStep !== 'form'}
                error={!!companyError}
                helperText={companyError}
              />
            </Box>

            {/* Username - full width */}
            {/* <Box sx={{ mb: 2 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Choose a Username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    disabled={isSubmitting || verificationStep !== 'form'}
                                    helperText="Use your email address or create a unique username"
                                />
                            </Box> */}

            {/* Password - full width */}
            <Box sx={{ mb: 2 }}>
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                disabled={isSubmitting || verificationStep !== 'form'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={isSubmitting || verificationStep !== 'form'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Confirm Password - full width */}
            <Box sx={{ mb: 2 }}>
              <TextField
                required
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isSubmitting || verificationStep !== 'form'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={isSubmitting || verificationStep !== 'form'}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Checkbox row */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    color="primary"
                    disabled={isSubmitting || verificationStep !== 'form'}
                  />
                }
                label={
                  <span>
                    I agree to the{' '}
                    <a
                      href={termsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {termsAndPrivacyLabel}
                    </a>
                  </span>
                }
              />
            </Box>

            {/* Buttons row */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                type="button" // Important: type="button" prevents form submission
                variant="outlined" // Outlined style for cancel button
                color="secondary"
                size="large"
                onClick={handleCancel}
                disabled={isSubmitting || verificationStep !== 'form'}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting || verificationStep !== 'form'}
                sx={{ minWidth: 120 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Sign Up'}
              </Button>
            </Box>
          </Box>

          {/* Domain correction alert */}
          {getDomainCorrection(formData.email) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              It looks like you may have mistyped the domain, did you mean <strong>{getDomainCorrection(formData.email)}</strong>?
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
}
